package com.dotdrives.client

import com.dotdrives.model.*
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put

/** Reads one complete, server-aggregated snapshot; no donation row list is transferred or capped. */
class GivingSummaryClient(private val client: SupabaseClient = SupabaseProvider.client, private val today:()->LocalDate={ Clock.System.now().toLocalDateTime(TimeZone.UTC).date }) {
    suspend fun load(orgId: String, filter: ReportFilter): GivingSummary {
        val asOf=minOf(filter.period.endInclusive,today())
        val row = client.postgrest.rpc("reports_snapshot", buildJsonObject {
            put("p_org", orgId); put("p_start", filter.period.start.toString()); put("p_end", filter.period.endInclusive.toString())
            put("p_as_of",asOf.toString()); put("p_prior_end", sameDatePriorYear(asOf).toString())
            filter.targetId?.let { put("p_target", it) }; filter.stewardUserId?.let { put("p_steward", it) }
        }).decodeAs<SnapshotRow>()
        return row.toModel(filter.period)
    }
    suspend fun load(orgId: String, year: Int, targetId: String?): GivingSummary =
        load(orgId, ReportFilter(fiscalYearPeriod(year, 1, 1), ReportPeriodKind.FiscalYear, targetId))
}

@Serializable private data class SnapshotRow(
    @SerialName("period_start") val periodStart: LocalDate, @SerialName("period_end") val periodEnd: LocalDate,
    val raised: Double, @SerialName("prior_raised") val priorRaised: Double,
    @SerialName("gift_count") val giftCount: Int, @SerialName("recurring_amount") val recurringAmount: Double,
    @SerialName("org_goal") val orgGoal: Double? = null,
    @SerialName("org_goal_start") val orgGoalStart:LocalDate?=null,@SerialName("org_goal_end") val orgGoalEnd:LocalDate?=null,
    @SerialName("as_of") val asOf:LocalDate,
    @SerialName("has_any_gifts") val hasAnyGifts:Boolean,
    val targets: List<TargetRow>, val stewards: List<StewardRow>, val months: List<MonthRow>, @SerialName("prior_months") val priorMonths: List<PriorMonthRow> = emptyList(),
    @SerialName("target_catalog") val targetCatalog:List<TargetRow> = emptyList(),@SerialName("steward_catalog") val stewardCatalog:List<StewardRow> = emptyList(),
) {
    fun toModel(period: ReportPeriod) = GivingSummary(
        targets=targets.map(TargetRow::toModel), orgBudget=OrgBudgetSummary(raised,orgGoal,orgGoalStart,orgGoalEnd),
        stewards=stewards.map(StewardRow::toModel),
        months=months.map { val date=LocalDate.parse(it.month); MonthlyGivingSummary(date.year,date.month.ordinal+1,MONTH_NAMES[date.month.ordinal],it.gifts,it.online,it.manual,it.recurring) },
        period=period, priorRaisedAmount=priorRaised, giftCount=giftCount, recurringAmount=recurringAmount,
        priorMonths=priorMonths.map { val date=LocalDate.parse(it.month); MonthlyGivingSummary(date.year,date.month.ordinal+1,MONTH_NAMES[date.month.ordinal],0,it.total,0.0,0.0) },
        targetCatalog=targetCatalog.map(TargetRow::toModel),stewardCatalog=stewardCatalog.map(StewardRow::toModel),asOf=asOf,hasAnyGifts=hasAnyGifts,
    )
}
@Serializable private data class TargetRow(val id:String,val name:String,val raised:Double,@SerialName("goal_amount") val goalAmount:Double?=null,@SerialName("goal_starts_on") val goalStart:LocalDate?=null,@SerialName("goal_ends_on") val goalEnd:LocalDate?=null) { fun toModel()=TargetGivingSummary(id,name,raised,goalAmount,goalStart,goalEnd) }
@Serializable private data class StewardRow(@SerialName("user_id") val userId:String,val name:String,val raised:Double,val budget:Double?=null,@SerialName("budget_start") val budgetStart:LocalDate?=null,@SerialName("budget_end") val budgetEnd:LocalDate?=null) { fun toModel()=StewardGivingSummary(userId,name.ifBlank{"Former steward"},raised,budget,budgetStart,budgetEnd) }
@Serializable private data class MonthRow(val month:String,val gifts:Int,val online:Double,val manual:Double,val recurring:Double,val total:Double)
@Serializable private data class PriorMonthRow(val month:String,val total:Double)
